import { strings, type StringKey } from './strings.js';

export { strings, type StringKey };

export const LOCALES = ['en', 'he'] as const;
export type Locale = (typeof LOCALES)[number];
export type Dir = 'ltr' | 'rtl';
export type Vars = Record<string, string | number>;

const INDEX: Record<Locale, 0 | 1> = { en: 0, he: 1 };

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Text direction for a UI locale. Timecodes, timelines and media stay LTR regardless (see `LTR`). */
export function dirOf(locale: Locale): Dir {
  return locale === 'he' ? 'rtl' : 'ltr';
}

/** Direction to force on media, timecodes, waveforms and code-like strings in every locale. */
export const LTR: Dir = 'ltr';

/** Fill {placeholders}; unknown placeholders are left visible so a missing var is easy to spot. */
export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}

/** Translate one key. Keys are type-checked against the string table. */
export function t(locale: Locale, key: StringKey, vars?: Vars): string {
  const entry = strings[key];
  return interpolate(entry[INDEX[locale]], vars);
}

export type TFunction = (key: StringKey, vars?: Vars) => string;

/** Bind t() to a locale, for components and hooks. */
export function createT(locale: Locale): TFunction {
  return (key, vars) => t(locale, key, vars);
}

/** Look up a key that is built at runtime (e.g. `role_${role}`), falling back to the raw value. */
export function tDynamic(locale: Locale, key: string, fallback?: string, vars?: Vars): string {
  if (key in strings) return t(locale, key as StringKey, vars);
  return fallback ?? key;
}

export function hasKey(key: string): key is StringKey {
  return key in strings;
}

/** BCP-47 tag for Intl formatters. Hebrew uses the Israeli calendar conventions (dd.mm.yyyy, 24 h). */
export function intlTag(locale: Locale): string {
  return locale === 'he' ? 'he-IL' : 'en-GB';
}

export function formatDate(locale: Locale, value: string | number | Date, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(intlTag(locale), opts).format(d);
}

export function formatNumber(locale: Locale, value: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(intlTag(locale), opts).format(value);
}

/** Wrap text in Unicode first-strong isolates so English/numbers inside Hebrew keep their punctuation in place. */
export function isolate(text: string): string {
  return `⁨${text}⁩`;
}
